export interface PlainLanguageGuidance {
  plainMeaning: string;
  actionItems: string[];
  spanishMeaning: string;
  spanishActions: string[];
}

export function getPlainLanguageGuidance(event: string, severity: string): PlainLanguageGuidance {
  const ev = event.toLowerCase();

  if (ev.includes('tornado warning')) {
    return {
      plainMeaning: 'A tornado has been sighted by trained spotters or indicated by Doppler weather radar. Danger to life and property is imminent.',
      actionItems: [
        'TAKE IMMEDIATE SHELTER: Go to a basement or interior room on the lowest floor.',
        'Stay away from all windows, skylights, and outer walls.',
        'Protect your head with a helmet, mattress, or thick blankets.',
        'If in a mobile home or vehicle, evacuate immediately to a sturdy shelter.',
      ],
      spanishMeaning: 'AVISO DE TORNADO: Un tornado ha sido detectado por radar o confirmado visualmente. El peligro para la vida es inminente.',
      spanishActions: [
        'BUSQUE REFUGIO DE INMEDIATO en el sótano o habitación interior del piso más bajo.',
        'Aléjese de ventanas, cristales y paredes exteriores.',
        'Proteja su cabeza con un casco, colchón o mantas pesadas.',
      ],
    };
  }

  if (ev.includes('tornado watch')) {
    return {
      plainMeaning: 'Atmospheric conditions are favorable for the development of tornadoes in and near the watch area over the coming hours.',
      actionItems: [
        'Be prepared to take shelter quickly if a warning is issued.',
        'Keep your NOAA weather radio, television, or phone alerts active.',
        'Identify your designated safe room now.',
      ],
      spanishMeaning: 'VIGILANCIA DE TORNADO: Las condiciones meteorológicas son favorables para el desarrollo de tornados en las próximas horas.',
      spanishActions: [
        'Esté preparado para refugiarse si se emite un aviso.',
        'Mantenga encendida la radio meteorológica o las alertas en su teléfono.',
        'Identifique su refugio seguro ahora.',
      ],
    };
  }

  if (ev.includes('flash flood') || ev.includes('flood warning')) {
    return {
      plainMeaning: 'Rapid and dangerous flooding of small streams, urban roads, underpasses, and low-lying areas is occurring or imminent.',
      actionItems: [
        'Never drive or walk through flooded roadways ("Turn Around, Don\'t Drown").',
        'Just 6 inches of moving water can knock you down, and 12 inches can sweep away a car.',
        'Move to higher ground immediately if in a flood-prone area.',
        'Keep children and pets away from storm drains and swollen creeks.',
      ],
      spanishMeaning: 'AVISO DE INUNDACIÓN REPENTINA: Inundación peligrosa en carreteras, zonas bajas y pasos a desnivel.',
      spanishActions: [
        'Nunca conduzca por carreteras inundadas ("Dé la vuelta, no se ahogue").',
        'Muévase a terreno más alto de inmediato si está en zona inundable.',
        'Aléjese de canales de agua, ríos y alcantarillas.',
      ],
    };
  }

  if (ev.includes('winter storm') || ev.includes('blizzard') || ev.includes('ice storm')) {
    return {
      plainMeaning: 'Hazardous winter conditions with heavy snow, blowing winds, freezing rain, or severe ice accumulation that makes travel dangerous or impossible.',
      actionItems: [
        'Avoid all non-essential road travel until roads are cleared and treated.',
        'Prepare for potential power grid interruptions with flashlights and extra blankets.',
        'Never run a fuel-burning generator indoors or inside a garage (carbon monoxide risk).',
        'Bring companion pets indoors.',
      ],
      spanishMeaning: 'AVISO DE TORMENTA INVERNAL / VENTISCA: Condiciones peligrosas de nieve, hielo y fuertes vientos que imposibilitan viajar.',
      spanishActions: [
        'Evite viajar por carretera a menos que sea una emergencia extrema.',
        'Prepárese para cortes de energía con linternas y cobijas gruesas.',
        'Nunca use generadores de gasolina dentro del hogar o garaje.',
      ],
    };
  }

  if (ev.includes('earthquake')) {
    return {
      plainMeaning: 'Seismic ground shaking detected by the USGS network. Aftershocks may follow in the minutes, hours, or days ahead.',
      actionItems: [
        'DROP to hands and knees, COVER your head and neck under a sturdy table, and HOLD ON.',
        'If outdoors, move to an open area away from power lines, chimneys, and tall buildings.',
        'If driving, pull over safely away from overpasses and bridges.',
      ],
      spanishMeaning: 'EVENTO SÍSMICO: Temblor detectado por el Servicio Geológico de EE.UU. (USGS). Pueden ocurrir réplicas.',
      spanishActions: [
        'AGÁCHESE, CÚBRASE debajo de una mesa firme y SUJÉTESE.',
        'Aléjese de ventanas, estantes altos y cables eléctricos.',
        'Si está conduciendo, oríllese con seguridad lejos de puentes.',
      ],
    };
  }

  if (ev.includes('red flag') || ev.includes('fire weather')) {
    return {
      plainMeaning: 'Critical wildfire conditions due to high winds, very low humidity, and warm temperatures. Any spark can cause an explosive fire spread.',
      actionItems: [
        'Observe all open burning bans. Do not throw cigarettes outside.',
        'Avoid operating equipment that creates sparks near dry grass or brush.',
        'Ensure tow chains on trailers are secure and not dragging on pavement.',
      ],
      spanishMeaning: 'AVISO DE BANDERA ROJA (PELIGRO DE INCENDIO): Condiciones críticas de viento y sequedad con alto riesgo de incendios forestales rápidos.',
      spanishActions: [
        'Prohibición total de fogatas o quema al aire libre.',
        'No tire colillas de cigarrillos al suelo.',
        'Evite usar maquinaria que genere chispas cerca de pasto seco.',
      ],
    };
  }

  // Fallback for general weather statements
  return {
    plainMeaning: `Official alert issued by public authorities: ${event}. Weather conditions in your zone require heightened awareness.`,
    actionItems: [
      'Monitor local broadcast weather and NOAA radio channels for updates.',
      'Check travel corridors and outdoor plans for disruption.',
      'Heed all warnings and instructions from local law enforcement and emergency managers.',
    ],
    spanishMeaning: `Aviso oficial emitido por autoridades: ${event}. Se requiere precaución en su área.`,
    spanishActions: [
      'Manténgase informado a través de los medios locales y radio meteorológica.',
      'Revise sus planes de viaje y actividades al aire libre.',
      'Siga las indicaciones de las autoridades locales de emergencia.',
    ],
  };
}

